import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { platformSurfaces } from '../data/features';
import { Reveal, Stagger, staggerItem } from './MotionPrimitives';

export default function PlatformSurfaces() {
  const [active, setActive] = useState(0);
  const current = platformSurfaces[active];

  return (
    <section className="section-pad bg-ink">
      <div className="mx-auto max-w-7xl">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="pill-light">Platform</span>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
            Four apps. <span className="grad-text">One business.</span>
          </h2>
          <p className="mt-4 text-mute">
            Control panel for you, shopping app for customers, delivery app for riders, and a shared
            backend that keeps every store in sync.
          </p>
        </Reveal>

        <Stagger className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {platformSurfaces.map((s, i) => {
            const selected = active === i;
            return (
              <motion.button
                key={s.id}
                type="button"
                variants={staggerItem}
                onClick={() => setActive(i)}
                className={`relative rounded-2xl border p-4 text-left transition-all ${
                  selected
                    ? 'border-accent/50 bg-accent/10 shadow-glow'
                    : 'border-white/10 bg-card hover:border-white/25'
                }`}
              >
                <AnimatePresence>
                  {selected && (
                    <motion.span
                      key="tick"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                      className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-grad-btn text-[11px] font-bold text-white shadow-glow ring-2 ring-black"
                      aria-hidden
                    >
                      ✓
                    </motion.span>
                  )}
                </AnimatePresence>
                <p className="text-xs font-semibold text-accent">{s.label}</p>
                <p className="mt-1 font-bold text-white">{s.name}</p>
                <p className="mt-1 text-xs text-faint">{s.product}</p>
              </motion.button>
            );
          })}
        </Stagger>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mt-8 rounded-2xl border border-white/10 bg-card p-6 sm:p-10"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-accent">
              {current.product}
            </p>
            <h3 className="mt-2 text-2xl font-bold text-white sm:text-3xl">{current.name}</h3>
            <p className="mt-2 text-lg text-accent-soft">{current.tagline}</p>
            <p className="mt-4 max-w-3xl text-mute">{current.description}</p>
            <ul className="mt-8 grid gap-2 sm:grid-cols-2">
              {current.notes.map((n) => (
                <li
                  key={n}
                  className="flex gap-2 rounded-xl border border-white/5 bg-black/40 px-3 py-2.5 text-sm text-mute"
                >
                  <span className="text-emerald-400">✓</span>
                  {n}
                </li>
              ))}
            </ul>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
